import React from "react";

/**
 * Brand logo — Péptidos Costa Rica
 * Circular badge with atomic symbol inside.
 * - Perfect circle (no ellipse)
 * - Atomic orbits with subtle 3D-feel rotation
 * - Pulsing nucleus + halo
 */
const BrandLogo = ({ size = 48, showText = true, className = "" }) => {
  const uid = React.useId().replace(/:/g, "");

  return (
    <div className={`inline-flex items-center gap-3 group ${className}`} aria-label="Péptidos Costa Rica">
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        xmlns="http://www.w3.org/2000/svg"
        className="shrink-0 transition-transform duration-500 group-hover:scale-110"
        aria-hidden="true"
      >
        <defs>
          <radialGradient id={`pill-${uid}`} cx="35%" cy="30%" r="80%">
            <stop offset="0%" stopColor="#4DFFB0" />
            <stop offset="50%" stopColor="#00DF82" />
            <stop offset="100%" stopColor="#007A48" />
          </radialGradient>
          <radialGradient id={`glow-${uid}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#00DF82" stopOpacity="0.55" />
            <stop offset="70%" stopColor="#00DF82" stopOpacity="0.05" />
            <stop offset="100%" stopColor="#00DF82" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Outer ambient glow (extends beyond the disc) */}
        <circle cx="50" cy="50" r="50" fill={`url(#glow-${uid})`} />

        {/* The circular disc */}
        <circle cx="50" cy="50" r="42" fill={`url(#pill-${uid})`} />

        {/* Specular highlight (top-left) */}
        <ellipse cx="35" cy="30" rx="14" ry="10" fill="#FFFFFF" opacity="0.18" transform="rotate(-30 35 30)" />

        {/* Atomic symbol — perfectly symmetric */}
        <g transform="translate(50 50)" fill="none" stroke="#0A0A0F" strokeWidth="2.8" strokeLinecap="round">
          {/* Two mirrored elliptical orbits, very subtle rotation for life */}
          <g>
            <ellipse cx="0" cy="0" rx="11" ry="28" transform="rotate(30)" />
            <ellipse cx="0" cy="0" rx="11" ry="28" transform="rotate(-30)" />
            <animateTransform attributeName="transform" type="rotate" from="0" to="360" dur="22s" repeatCount="indefinite" additive="sum" />
          </g>

          {/* Nucleus */}
          <circle r="3.2" fill="#0A0A0F">
            <animate attributeName="r" values="2.8;3.8;2.8" dur="2.6s" repeatCount="indefinite" />
          </circle>
          {/* Pulsing halo */}
          <circle r="3.2" fill="none" stroke="#0A0A0F" strokeWidth="1.2" opacity="0.55">
            <animate attributeName="r" values="3;10;3" dur="2.6s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.55;0;0.55" dur="2.6s" repeatCount="indefinite" />
          </circle>
        </g>
      </svg>

      {showText && (
        <div className="flex flex-col leading-none">
          <span className="font-display font-medium text-base sm:text-lg tracking-tight text-white">Péptidos</span>
          <span className="font-display text-[10px] tracking-[0.32em] uppercase text-[#8F8F99] mt-1">Costa Rica</span>
        </div>
      )}
    </div>
  );
};

export default BrandLogo;
